import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import * as yup from 'yup';

import { Card } from 'components/Card/Card';
import Button from 'components/Button/Button';
import Tooltip from 'components/Tooltip/Tooltip';

import colors from 'theme/Colors.module.scss';
import './UserInvite.scss';

import {
  Box,
  Text,
  Flex,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
  ListItem,
  UnorderedList,
} from '@chakra-ui/react';

import { useCreateInvitationMutation } from 'modules/users/usersApi';

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email.')
    .required('Email is required.')
    .trim(),
});

export default props => {
  const [invitations, setInvitations] = useState([]);

  const defaults = {
    email: '',
  };

  // Selectors
  const sessionUser = useSelector(state => state.app.user);

  const [createInvitation] = useCreateInvitationMutation();

  const addInvitation = invitation => {
    setInvitations(invitations => invitations.concat(invitation));
  };

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);

    try {
      const invitation = await createInvitation({
        email: values.email,
        company_id: sessionUser.company_id,
      }).unwrap();
      addInvitation(invitation);
    } catch (err) {
      console.error(err);
    } finally {
      form.setSubmitting(false);
      form.setValues(defaults);
      form.setTouched({});
    }
  };

  return (
    <>
      <Box className="user-invite-header">
        <Box className="user-invite-header__left">
          <Button
            type="button"
            url="/users"
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="user-invite-header__nav">
          <Text as="p" fontWeight="600">
            Invite user
          </Text>
        </Box>
        <Box className="user-invite-header__right"></Box>
      </Box>
      <Formik
        enableReinitialize={true}
        initialValues={defaults}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {form => {
          const { isSubmitting } = form;

          return (
            <Form className="user-invite-form__container">
              <Card className="user-invite-form__card">
                <Flex flexDirection="column" gap={8}>
                  <Flex flexDirection="column">
                    <Field name="email">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={form.errors.email && form.touched.email}
                        >
                          <FormLabel>
                            Email&nbsp;
                            <Tooltip
                              tipText="An email with an invitation to join will be sent to the provided address."
                              direction="top"
                            />
                          </FormLabel>
                          <Input
                            {...field}
                            type="email"
                            placeholder="Email*"
                            focusBorderColor={colors.primary600}
                          />
                          <FormErrorMessage>
                            {form.errors.email}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>

                  {invitations.length > 0 && (
                    <>
                      <hr
                        style={{
                          marginTop: '0',
                          marginBottom: '-0.5rem',
                        }}
                      />
                      <Flex flexDirection="column">
                        <Text as="p">You've invited:</Text>
                        <UnorderedList>
                          {invitations.map((invitation, index) => {
                            return (
                              <ListItem key={index}>
                                {invitation.email}
                              </ListItem>
                            );
                          })}
                        </UnorderedList>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Card>

              <Flex flexDirection="column" align="center">
                <Button
                  icon="paper-plane"
                  iconColor={colors.white}
                  type="submit"
                  text="Send Invitation"
                  textColor={colors.white}
                  btnStyle="solid"
                  hoverColor={colors.primary500}
                  textHoverColor={colors.white}
                  disabled={isSubmitting}
                />
                <Text as="p" className="footnote">
                  An asterisk (*) marks a required field.
                </Text>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
